import { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import emailService from '../services/emailService';
import { ensureTenantId, TENANT_ID_STORAGE_KEY } from '../utils/tenantUtils';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load user from localStorage on initial mount
    const loadUserFromStorage = () => {
      try {
        const storedUser = localStorage.getItem('authUser');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && (parsedUser._id || parsedUser.id)) {
            console.log('Loaded user from localStorage:', parsedUser);
            setUser(parsedUser);

            // Ensure userId is also set in localStorage for API requests
            localStorage.setItem('userId', parsedUser._id || parsedUser.id);
            
            // Also set tenant ID if available in user object
            if (parsedUser.tenantId) {
              ensureTenantId(parsedUser.tenantId);
            }
            
            return true;
          }
        }
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
        localStorage.removeItem('authUser');
      }
      return false;
    };

    // Try to load from storage
    const userLoaded = loadUserFromStorage();
    
    // Check for valid token regardless of stored user
    const token = localStorage.getItem('token');
    
    if (token && !userLoaded) {
      // If we have a token but no user, fetch user info
      try {
        // Try to get the current user - Wrap with try/catch instead of using .then/.catch
        const fetchCurrentUser = async () => {
          try {
            // Check if getCurrentUser exists and is a function
            if (typeof authService.getCurrentUser === 'function') {
              // Handle both Promise and non-Promise returns
              const result = authService.getCurrentUser();
              
              let userData;
              if (result && typeof result.then === 'function') {
                // It's a Promise
                userData = await result;
              } else {
                // It's a direct return value
                userData = result;
              }
              
              if (userData) {
                setUser(userData);
                // Ensure userId is set in localStorage for API requests
                localStorage.setItem('userId', userData._id || userData.id);
                localStorage.setItem('authUser', JSON.stringify(userData));
              }
            } else {
              console.warn('authService.getCurrentUser is not a function');
            }
          } catch (err) {
            console.error('Error fetching current user:', err);
            localStorage.removeItem('token');
          } finally {
            setLoading(false);
          }
        };
        
        fetchCurrentUser();
      } catch (error) {
        console.error('Error in auth effect:', error);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Add this function to the AuthContext provider component
  const checkAndRefreshSession = async () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      // Verify if the current token is valid
      const response = await authService.validateToken();
      return true;
    } catch (error) {
      console.log('Token validation failed, attempting to refresh...');
      
      try {
        // Try to refresh the token
        const refreshResponse = await authService.refreshToken();
        return !!refreshResponse.token;
      } catch (refreshError) {
        console.error('Session refresh failed:', refreshError);
        return false;
      }
    }
  };

  // Add an effect to periodically check and refresh the session
  useEffect(() => {
    let sessionCheckInterval = null;
    
    if (user) {
      // Check session every 15 minutes
      sessionCheckInterval = setInterval(() => {
        checkAndRefreshSession()
          .then(isValid => {
            if (!isValid) {
              console.warn('Session validation failed after refresh attempt');
            }
          });
      }, 15 * 60 * 1000); // 15 minutes
    }
    
    return () => {
      if (sessionCheckInterval) clearInterval(sessionCheckInterval);
    };
  }, [user]);

  const login = async (email, password) => {
    try {
      setError(null);
      console.log('AuthContext: Logging in with email:', email);
      
      // Call the login function from authService
      const result = await authService.login(email, password);
      
      console.log('AuthContext: Login successful, got result:', {
        hasUser: !!result.user,
        hasToken: !!result.token,
        hasTenant: !!result.tenant,
        passwordResetRequired: !!result.passwordResetRequired,
        userFields: result.user ? Object.keys(result.user) : []
      });
      
      // Check if we got a user object
      if (!result.user) {
        console.error('AuthContext: No user data in login response', result);
        throw new Error('No user data in response');
      }
      
      // Make sure user has passwordResetRequired property explicitly set
      const userData = {
        ...result.user,
        passwordResetRequired: !!result.passwordResetRequired
      };
      
      // Set user state from the response
      setUser(userData);
      
      // Return the complete user data including passwordResetRequired flag
      return userData;
    } catch (error) {
      console.error('Login error details:', error);
      
      // More detailed error handling
      let errorMessage = error.message || 'An error occurred during login';
      
      // Special handling for startup-related errors
      if (
        errorMessage.includes('starting up') || 
        errorMessage.includes('initializing') || 
        errorMessage.includes('Startup in progress')
      ) {
        setError('The system is starting up. Please wait a moment and try again.');
        throw new Error('The system is starting up. Please wait a moment and try again.');
      }
      
      // Handle specific API errors
      if (error.response) {
        console.error('Login API error response:', error.response);
        errorMessage = error.response.data?.message || 
                      error.response.data?.error || 
                      `Server error: ${error.response.status}`;
                      
        // If server is returning a plain text error like "Startup in progress"
        if (typeof error.response.data === 'string' && 
            (error.response.data.includes('startup') || 
             error.response.data.includes('initializing') || 
             error.response.data.includes('starting'))) {
          errorMessage = 'The system is starting up. Please wait a moment and try again.';
        }
      } else if (error.request) {
        console.error('Login network error - no response received:', error.request);
        errorMessage = 'Network error - unable to connect to the server. Please check your internet connection.';
      }
      
      setError(errorMessage);
      throw error;
    }
  };

  const logout = () => {
    try {
      // Call the logout API endpoint if available
      if (typeof authService.logout === 'function') {
        authService.logout().catch(err => console.error('Logout API error:', err));
      }
      
      // Clear all authentication and session data from localStorage
      const itemsToRemove = [
        'token', 'authUser', 'userId', 'isAdmin', 'refreshToken',
        'currentTenant', 'tenantId', TENANT_ID_STORAGE_KEY,
        'user', 'lastLoginTime', 'permissions', 'userPreferences',
        'resetEmail'
      ];
      
      // Clear all localStorage items
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear cookies (if available)
      document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.trim().split('=');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
      
      // Reset application state
      setUser(null);
      setError(null);
      
      // Force a complete page reload to ensure clean state
      if (window.location) {
        // Add a random parameter to prevent caching
        window.location.href = '/login?clear=' + new Date().getTime();
      }
      
      console.log('User successfully logged out with complete cache and cookie clearing');
      return true;
    } catch (error) {
      console.error('Error during logout:', error);
      
      // Even if there's an error, try to clear everything and reload
      try {
        localStorage.clear();
        sessionStorage.clear();
        
        // Force clearing cookies
        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.trim().split('=');
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        
        if (window.location) {
          window.location.href = '/login?clear=' + new Date().getTime();
        }
      } catch (innerError) {
        console.error('Error during emergency cleanup:', innerError);
      }
      
      return false;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const data = await authService.register(userData);
      
      if (data.user && data.token) {
        // Store token and user data
        localStorage.setItem('token', data.token);
        localStorage.setItem('authUser', JSON.stringify(data.user));
        
        // Ensure userId is set separately for API calls
        localStorage.setItem('userId', data.user._id || data.user.id);
        
        setUser(data.user);
        return data.user;
      }
      
      throw new Error('Registration response missing user or token');
    } catch (error) {
      setError(error.message || 'An error occurred during registration');
      throw error;
    }
  };

  const forgotPassword = async (email) => {
    try {
      // Store the email temporarily for the confirmation
      localStorage.setItem('resetEmail', email);
      const response = await emailService.sendPasswordResetLink(email);
      return response;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await emailService.resetPassword(token, newPassword);
      return response;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};